import { useState, useEffect } from 'react';
import { useBranding } from '@/hooks/useBranding';
import { apiUpload, API_URL } from '@/lib/api-client';

export function BrandingSettings() {
  const { branding, updateBranding } = useBranding();
  const [localBranding, setLocalBranding] = useState(branding);

  // Sync local state when branding loads from API
  useEffect(() => {
    setLocalBranding(branding);
  }, [branding]);
  const [uploading, setUploading] = useState<'logo' | 'favicon' | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleFileUpload = async (file: File, type: 'logo' | 'favicon') => {
    try {
      setUploading(type);
      setMessage(null);

      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Please upload an image file');
      }

      // Upload to API
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', type);

      const data = await apiUpload<{ url: string }>('/api/admin/branding/upload', formData);

      const publicUrl = data.url.startsWith('http') ? data.url : `${API_URL}${data.url}`;

      // Update local state
      if (type === 'logo') {
        setLocalBranding((prev) => ({ ...prev, logoUrl: publicUrl }));
      } else {
        setLocalBranding((prev) => ({ ...prev, faviconUrl: publicUrl }));
      }

      setMessage({ type: 'success', text: `${type === 'logo' ? 'Logo' : 'Favicon'} uploaded successfully!` });
    } catch (err: any) {
      console.error('Upload error:', err);
      setMessage({ type: 'error', text: err.message || 'Upload failed' });
    } finally {
      setUploading(null);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      await updateBranding(localBranding);

      setMessage({ type: 'success', text: 'Branding saved successfully!' });
    } catch (err) {
      console.error('Error saving branding:', err);
      setMessage({ type: 'error', text: 'Failed to save branding' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white mb-2">Branding Settings</h2>
        <p className="text-slate-400 text-sm">Customize your application's logo, favicon, and name</p>
      </div>

      {message && (
        <div
          className={`p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
              : 'bg-red-500/10 border border-red-500/30 text-red-400'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Logo Upload */}
      <div className="glass-strong p-6 rounded-xl">
        <h3 className="text-lg font-semibold text-white mb-4">Application Logo</h3>

        <div className="flex items-start gap-6">
          <div className="flex-shrink-0">
            <div className="w-32 h-32 bg-charcoal-800 rounded-xl border-2 border-charcoal-600 flex items-center justify-center overflow-hidden">
              {localBranding.logoUrl ? (
                <img
                  src={localBranding.logoUrl}
                  alt="Logo preview"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.currentTarget.src = '/favicon.svg';
                  }}
                />
              ) : (
                <svg className="w-16 h-16 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              )}
            </div>
          </div>

          <div className="flex-1">
            <input
              type="file"
              id="logo-upload"
              accept="image/*"
              onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'logo')}
              className="hidden"
            />
            <label
              htmlFor="logo-upload"
              className="inline-flex items-center gap-2 px-4 py-2 bg-charcoal-700 hover:bg-charcoal-600
                       text-white font-medium rounded-lg cursor-pointer transition-colors"
            >
              {uploading === 'logo' ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  Upload Logo
                </>
              )}
            </label>
            <p className="text-xs text-slate-500 mt-2">Recommended: 512x512px PNG or SVG</p>
          </div>
        </div>
      </div>

      {/* Favicon Upload */}
      <div className="glass-strong p-6 rounded-xl">
        <h3 className="text-lg font-semibold text-white mb-4">Favicon</h3>

        <div className="flex items-start gap-6">
          <div className="flex-shrink-0">
            <div className="w-20 h-20 bg-charcoal-800 rounded-lg border-2 border-charcoal-600 flex items-center justify-center overflow-hidden">
              {localBranding.faviconUrl ? (
                <img
                  src={localBranding.faviconUrl}
                  alt="Favicon preview"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    e.currentTarget.src = '/favicon.svg';
                  }}
                />
              ) : (
                <svg className="w-10 h-10 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              )}
            </div>
          </div>

          <div className="flex-1">
            <input
              type="file"
              id="favicon-upload"
              accept="image/*"
              onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'favicon')}
              className="hidden"
            />
            <label
              htmlFor="favicon-upload"
              className="inline-flex items-center gap-2 px-4 py-2 bg-charcoal-700 hover:bg-charcoal-600
                       text-white font-medium rounded-lg cursor-pointer transition-colors"
            >
              {uploading === 'favicon' ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  Upload Favicon
                </>
              )}
            </label>
            <p className="text-xs text-slate-500 mt-2">Recommended: 32x32px or 64x64px ICO/PNG</p>
          </div>
        </div>
      </div>

      {/* App Name */}
      <div className="glass-strong p-6 rounded-xl">
        <h3 className="text-lg font-semibold text-white mb-4">Application Name</h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">App Name</label>
            <input
              type="text"
              value={localBranding.appName}
              onChange={(e) => setLocalBranding((prev) => ({ ...prev, appName: e.target.value }))}
              placeholder="LevelUp Coach"
              className="w-full px-4 py-2 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white
                       focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none
                       placeholder:text-slate-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Tagline</label>
            <input
              type="text"
              value={localBranding.tagline}
              onChange={(e) => setLocalBranding((prev) => ({ ...prev, tagline: e.target.value }))}
              placeholder="Faith. Family. Finance. Fitness."
              className="w-full px-4 py-2 bg-charcoal-800 border border-charcoal-600 rounded-lg text-white
                       focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 outline-none
                       placeholder:text-slate-600"
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-3 bg-royal-gradient text-white font-medium rounded-xl
                   hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed
                   shadow-glow flex items-center gap-2"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Save Branding
            </>
          )}
        </button>
      </div>
    </div>
  );
}
